import type { TFunction } from 'i18next';
import i18n from '../../i18n';
import { AppError, isAppError } from './AppError';

type TranslateFn = TFunction | ((key: string, options?: Record<string, unknown>) => string);

/** Codes Firebase (Firestore / Functions HttpsError) → clés `common.errors.*`. */
const FIREBASE_CODE_TO_COMMON: Record<string, string> = {
  'permission-denied': 'errors.permissionDenied',
  unauthenticated: 'errors.unauthenticated',
  'not-found': 'errors.notFound',
  'already-exists': 'errors.alreadyExists',
  'failed-precondition': 'errors.failedPrecondition',
  unavailable: 'errors.unavailable',
  'invalid-argument': 'errors.invalidArgument',
  'resource-exhausted': 'errors.unavailable',
  internal: 'errors.generic',
  'deadline-exceeded': 'errors.unavailable',
};

/**
 * Normalise un code Firebase (`functions/unauthenticated` → `unauthenticated`).
 */
export function normalizeFirebaseErrorCode(code: unknown): string | null {
  if (typeof code !== 'string' || !code.trim()) return null;
  const raw = code.trim();
  const withoutPrefix = raw.includes('/') ? raw.slice(raw.lastIndexOf('/') + 1) : raw;
  return withoutPrefix || null;
}

/**
 * Message i18n pour un code Firebase / Functions, ou null si inconnu.
 * Les messages FR renvoyés par Functions sont ignorés volontairement (mapping code → locale client).
 */
export function translateFirebaseErrorCode(
  code: unknown,
  t?: TranslateFn
): string | null {
  const normalized = normalizeFirebaseErrorCode(code);
  if (!normalized) return null;
  const commonKey = FIREBASE_CODE_TO_COMMON[normalized];
  if (!commonKey) return null;
  const translated = t
    ? String(t(commonKey, { ns: 'common', defaultValue: '' }))
    : String(i18n.t(commonKey, { ns: 'common', defaultValue: '' }));
  return translated || null;
}

function translateErrorCode(
  code: string,
  params?: Record<string, string | number>,
  t?: TranslateFn
): string {
  const options = { ns: 'errors', defaultValue: '', ...(params || {}) };
  const translated = t
    ? String(t(code, options))
    : String(i18n.t(code, options));
  return translated || code;
}

function readErrorCode(error: unknown): unknown {
  if (error && typeof error === 'object' && 'code' in error) {
    return (error as { code?: unknown }).code;
  }
  return undefined;
}

/**
 * Traduit une AppError / Error / string pour l'affichage UI.
 * - AppError → clé `errors:<code>`
 * - objet avec `code` Firebase/Functions → `common.errors.*` (ignore message serveur FR)
 * - Error.message ressemblant à une clé (`domain.key`) → tentative i18n
 * - sinon message brut ou erreur générique
 */
export function getErrorMessage(error: unknown, t?: TranslateFn): string {
  if (isAppError(error)) {
    return translateErrorCode(error.code, error.params, t);
  }

  const firebaseMsg = translateFirebaseErrorCode(readErrorCode(error), t);
  if (firebaseMsg) return firebaseMsg;

  if (typeof error === 'string') {
    if (error.includes('.') && !/\s/.test(error)) {
      const translated = translateErrorCode(error, undefined, t);
      if (translated !== error) return translated;
    }
    return error;
  }

  if (error instanceof Error) {
    const msg = error.message || '';
    if (msg.includes('.') && !/\s/.test(msg)) {
      const translated = translateErrorCode(msg, undefined, t);
      if (translated !== msg) return translated;
    }
    if (msg.trim()) return msg;
  }

  return (
    translateErrorCode('generic', undefined, t) ||
    String(
      i18n.t('errors.generic', {
        ns: 'common',
        defaultValue: 'Une erreur est survenue.',
      })
    )
  );
}

export function toAppError(
  code: string,
  params?: Record<string, string | number>
): AppError {
  return new AppError(code, params);
}
